import DOMPurify from 'dompurify'

import { type Address0 } from '~types'

export const stripHtmlTags = (html: string): string => {
  if (!html) return ''

  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, '')

  // Replace HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

  // Preserve line breaks and paragraphs
  text = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<p>/gi, '')

  // Remove extra whitespace
  text = text.replace(/\s+/g, ' ').trim()

  return text
}

export const sanitizeText = (text: string): string => {
  if (!text) return ''

  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
    if (node instanceof HTMLElement && node.style.whiteSpace) {
      node.style.removeProperty('white-space')
      if (!node.getAttribute('style')) node.removeAttribute('style')
    }
  })

  const sanitized = DOMPurify.sanitize(text, {
    ADD_ATTR: ['target', 'rel'],
  })

  DOMPurify.removeHook('afterSanitizeAttributes')

  return `<div class="styled-links">${sanitized}</div>`
}

const TITLE_CASE_EXCEPTIONS = ['and', 'of']
export const toTitleCase = (str: string): string => {
  return str
    .split('_')
    .map((word) =>
      TITLE_CASE_EXCEPTIONS.includes(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ')
}

export function oneLineAddress(address: Address0): string {
  return `${address.street}, ${address.city}, ${address.state} ${address.zip}`
}

export function oneLineAddressOrUndefined(address?: Address0): string {
  return address ? oneLineAddress(address) : ''
}

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function isValidUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false
  try {
    const url = new URL(value)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    const hostname = url.hostname.replace(/^www\./, '')
    const parts = hostname.split('.')
    return parts.length >= 2 && parts.every((part) => part.length > 0)
  } catch {
    return false
  }
}

export const slugify = (text: string, fallback = 'untitled', maxLength = 60): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, maxLength) || fallback
